import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { SubscriptionBlocker } from "@/components/SubscriptionBlocker";
import { LimitBlocker } from "@/components/LimitBlocker";
import { LimitBanner } from "@/components/LimitBanner";
import { SubscriptionGracePeriodBanner } from "@/components/SubscriptionGracePeriodBanner";
import { Sidebar } from "@/components/Sidebar";
import { ChatBot } from "@/components/chat/ChatBot";
import { GlobalSearch } from "@/components/search/GlobalSearch";
import { AIStatusBanner } from "@/components/AIStatusBanner";
import { Switch } from "@/components/ui/switch";
import { useSettings } from "@/providers/SettingsProvider";
import { Sun, Moon, Menu, Mail } from "lucide-react";
import LanguageSelector from "./LanguageSelector";
import { NotificationBell } from "./NotificationBell";
import { Sheet, SheetTrigger, SheetContent } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { useActiveAccounts } from "@/hooks/useActiveAccounts";
import { Logo } from "./sidebar/Logo";
import { Navigation } from "./sidebar/Navigation";
import { UserProfile } from "./sidebar/UserProfile";
import { Button } from "@/components/ui/button";
import { QuickPeekDrawer } from "@/components/inbox/QuickPeekDrawer";
import { TourProvider } from "@/providers/TourProvider";
import { GuidedTour } from "@/components/tour/GuidedTour";
import { TourHelpButton } from "@/components/tour/TourHelpButton";
import { useFeatureFlag } from "@/providers/FeatureFlagsProvider";

export const Layout = () => {
    const settings = useSettings();
    const aiChatEnabled = useFeatureFlag("aiChat");
    const isDarkMode = settings.colorMode === "dark"
        || (settings.colorMode === "auto" && window.matchMedia("(prefers-color-scheme: dark)").matches);
    const isMobile = useIsMobile();
    const { hasActiveAccounts } = useActiveAccounts();
    const location = useLocation();
    // Full-height pages manage their own panes and scrolling — no main padding/scroll.
    const FULL_HEIGHT_ROUTES = ["/inbox", "/transcripts"];
    const isFullHeightRoute = FULL_HEIGHT_ROUTES.some((path) => location.pathname.startsWith(path));
    const isInboxRoute = location.pathname.startsWith("/inbox");

    const [mailboxOpen, setMailboxOpen] = React.useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const drawerWidth = 420;
    const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
        const savedState = localStorage.getItem("sidebar-collapsed");
        return savedState === "true";
    });

    const sidebarWidth = sidebarCollapsed ? 80 : 240;

    // The quick-peek drawer is redundant on the full inbox page.
    React.useEffect(() => {
        if (isInboxRoute) setMailboxOpen(false);
    }, [isInboxRoute]);

    const toggleDarkMode = () => {
        settings.setColorModeValue(isDarkMode ? "light" : "dark");
        document.documentElement.classList.toggle("dark");
    };

    const handleNavigationClick = () => {
        if (isMobile) {
            setMobileMenuOpen(false);
        }
    };

    return (
        <TourProvider>
        <div className="h-screen w-screen bg-gradient-to-br from-background/80 to-background transition-colors duration-300">
            <SubscriptionBlocker />
            <LimitBlocker />
            <GuidedTour />

            {isMobile ? (
                <div className="flex flex-col h-full w-full overflow-hidden">

                    <div className="w-full bg-white/90 dark:bg-gray-900/90 border-b border-gray-100 dark:border-gray-800 flex items-center h-14 sticky top-0 z-20 px-2 py-2">

                        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>

                            <SheetTrigger asChild>
                                <button
                                    className="flex items-center justify-center rounded-sm bg-white/80 dark:bg-gray-800/80 p-2 border border-gray-200 dark:border-gray-700 focus:outline-none"
                                    aria-label="Open menu"
                                    data-tour="mobile-menu-toggle"
                                >
                                    <Menu className="h-6 w-6 text-gray-700 dark:text-gray-300" />

                                </button>
                            </SheetTrigger>
                            <SheetContent side="left" className="p-0 w-64 max-w-full">

                                <div className="flex flex-col h-full">
                                    <div className="flex-1 overflow-y-auto">

                                        <Navigation collapsed={false} orientation="vertical" onNavigationClick={handleNavigationClick} />

                                    </div>
                                    <div className="p-4 border-t">
                                        <UserProfile collapsed={false} />

                                    </div>
                                    <div className="border-t p-4 flex items-center gap-3">

                                        <Sun className="h-4 w-4 text-foreground/70" />

                                        <Switch checked={isDarkMode} onCheckedChange={toggleDarkMode} />

                                        <Moon className="h-4 w-4 text-foreground/70" />

                                    </div>
                                </div>
                            </SheetContent>
                        </Sheet>
                        <div className="flex-1 flex justify-center items-center min-w-0">

                            <Logo collapsed={false} />
                        </div>
                        <div className="flex items-center gap-2">
                            <TourHelpButton />
                            <NotificationBell />
                            {hasActiveAccounts && !mailboxOpen && !isInboxRoute && (
                                <Button variant="ghost" size="icon" onClick={() => setMailboxOpen(true)} title="Open mailbox" className="relative">
                                    <Mail className="h-5 w-5" />
                                </Button>
                            )}
                        </div>
                    </div>

                    <header className="border-b border-white/20 dark:border-white/10 p-4 bg-white/60 dark:bg-black/40 backdrop-blur-md sticky z-10 top-14">

                        <div className="w-full flex items-center gap-4">
                            <div className="flex-1 min-w-0 flex">
                                <GlobalSearch />
                            </div>
                        </div>
                        <SubscriptionGracePeriodBanner />
                    </header>
                    <main
                        className={cn(
                            "flex-1",
                            isFullHeightRoute
                                ? "flex min-h-0 flex-col overflow-hidden"
                                : "overflow-y-auto overflow-x-hidden p-4 md:p-6"
                        )}
                    >
                        <AIStatusBanner />
                        <LimitBanner />
                        {isFullHeightRoute ? (
                            <div className="min-h-0 flex-1">
                                <Outlet />
                            </div>
                        ) : (
                            <div className="w-full max-w-full mx-auto animate-fade-in">
                                <Outlet />
                            </div>
                        )}
                    </main>
                    <QuickPeekDrawer open={mailboxOpen} onOpenChange={setMailboxOpen} />
                    {aiChatEnabled && <ChatBot />}
                </div>
            ) : (
                <div
                    className="grid h-screen w-screen transition-[grid-template-columns] duration-300"
                    style={{
                        gridTemplateColumns: `${sidebarWidth}px 1fr ${mailboxOpen ? `${drawerWidth}px` : '0px'}`,
                        gridTemplateRows: '1fr',
                        overflow: 'hidden',
                    }}
                >
                    <div className="h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 z-40" style={{ gridColumn: 1, gridRow: 1, position: 'relative', width: `${sidebarWidth}px` }}>

                        <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
                    </div>
                    <div className="relative h-full w-full" style={{ gridColumn: 2, gridRow: 1, minWidth: 0, minHeight: 0, overflow: 'hidden' }}>

                        <header className="border-b border-white/20 dark:border-white/10 p-4 md:pl-12 md:pr-6 bg-white/60 dark:bg-black/40 backdrop-blur-md sticky z-10 top-0">

                            <div className="w-full flex items-center gap-4">
                                <div className="flex-1 min-w-0 flex">
                                    <GlobalSearch />
                                </div>
                                <div className="flex items-center gap-4 ml-auto">
                                    <TourHelpButton />
                                    <NotificationBell />
                                    {hasActiveAccounts && !mailboxOpen && !isInboxRoute && (
                                        <Button variant="ghost" size="icon" onClick={() => setMailboxOpen(true)} title="Open mailbox" className="relative">
                                            <Mail className="h-5 w-5" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <SubscriptionGracePeriodBanner />
                        </header>
                        <main
                            className={cn(
                                isFullHeightRoute
                                    ? "flex min-h-0 flex-col overflow-hidden"
                                    : "overflow-y-auto overflow-x-hidden p-4 md:pl-12 md:pr-6 md:pt-1 md:pb-6"
                            )}
                            style={{ height: 'calc(100vh - 64px)', minWidth: 0 }}
                        >
                            <AIStatusBanner />
                            <LimitBanner />
                            {isFullHeightRoute ? (
                                <div className="min-h-0 flex-1">
                                    <Outlet />
                                </div>
                            ) : (
                                <div className="w-full max-w-full mx-auto animate-fade-in">
                                    <Outlet />
                                </div>
                            )}
                        </main>
                        {aiChatEnabled && <ChatBot />}
                    </div>
                    {mailboxOpen && (
                        <div className="h-full border-l border-border bg-background shadow-xl z-40" style={{ gridColumn: 3, gridRow: 1, minWidth: 320, minHeight: 0, overflow: 'hidden', width: `${drawerWidth}px` }}>
                            <QuickPeekDrawer open={mailboxOpen} onOpenChange={setMailboxOpen} />
                        </div>
                    )}
                </div>
            )}
        </div>
        </TourProvider>
    );
};
